import { useEffect, useState } from 'react';

/**
 * navigator.onLine + 'online'/'offline' イベントを購読してオンライン状態を返す hook (user 第 12 弾、GAP-U5 MUST)。
 *
 * 用途:
 *   - AppHome / MapPage で「オフライン中」インジケータの出し分け (default = 表示なし、offline = notice bar)
 *   - useOutbox は内部で 'online' のみ購読 (復帰時 auto flush) しており、本 hook は UI 表示専用に独立。
 *     どちらも `window.addEventListener('online', ...)` を独立 subscribe するが、ブラウザ側は複数 listener
 *     を許容し、subscribe / cleanup も独立して動くため二重発火・leak の懸念なし
 *
 * SSR 安全性:
 *   - `typeof navigator === 'undefined'` のとき初期値を `true` (online 扱い) にする。SSR は v2 では未採用だが、
 *     test 環境 / vitest の jsdom 互換性 / 将来の SSR 化に備える保守
 *   - `typeof window === 'undefined'` のとき addEventListener も skip
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(() => {
    if (typeof navigator === 'undefined') return true;
    return navigator.onLine;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    function handleOnline() {
      setOnline(true);
    }
    function handleOffline() {
      setOnline(false);
    }

    // 初期値 useState lazy initializer で navigator.onLine を取得済。mount 時の再同期は
    // 'online'/'offline' event listener が即時拾うため不要 (react-hooks/set-state-in-effect 回避)
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return online;
}
